import { useCallback, useMemo } from 'react'
import { ActivityLevelValidationIcon } from '@/common/components/icons'
import ActivityLevelValidationReporting from './ActivityLevelValidationReporting'
import { ActivityLevelValidation } from '@/api/EmissionReportModel'
import useI18n from '@/shared/hooks/useI18n'
import { useDialog } from '@/shared/hooks/useDialog'
import ActivityLevelValidationDialog from './dialogs/ActivityLevelValidationDialog'
import { useEmissionReport, useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import ReportSectionCard from '../ReportSectionCard'
import validateActivityLevel from '@/features/reports/formulas/validateActivityLevel'
import { Alert } from '@/common'

export default function ActivityLevelValidationSection({
  reportId,
  isComplete,
  isPending,
  isViewMode,
  processId,
  activityLevelValidation,
  activityLevel,
  hasAnyPrecursor
}: {
  reportId: string
  isViewMode: boolean
  isComplete: boolean
  isPending?: boolean
  processId: string
  activityLevelValidation: ActivityLevelValidation | null
  activityLevel: number
  hasAnyPrecursor: boolean
}) {
  const { t } = useI18n()
  const { open, openDialog, closeDialog } = useDialog()
  const { data: emissionReport } = useEmissionReport(reportId)
  const { mutate: updateMethodEmissionReport } = useUpdateMethodEmissionReport(reportId, processId)
  const precursorMaterials = useMemo(
    () => emissionReport?.precursorMaterials || [],
    [emissionReport?.precursorMaterials]
  )
  const preConsumerScrapMaterials = useMemo(
    () => emissionReport?.preconsumerScrapMaterials || [],
    [emissionReport?.preconsumerScrapMaterials]
  )
  const onSave = useCallback(
    (payload: {
      hasToBeReEvaluated: boolean
      changeInStock: number
      conversions: number
      waste: number
      conversionsPercentage: number
    }) => {
      updateMethodEmissionReport({
        activityLevelValidation: {
          ...activityLevelValidation,
          ...payload
        }
      })
    },
    [activityLevelValidation, updateMethodEmissionReport]
  )

  const activityLevelValidationResult = useMemo(() => {
    return validateActivityLevel({
      activityLevel,
      changeInStock: activityLevelValidation?.changeInStock || 0,
      waste: activityLevelValidation?.waste || 0,
      precursorMaterials,
      preconsumerScrapMaterials: preConsumerScrapMaterials,
      endProductEmittingMethodInputs: emissionReport?.endProductEmittingMethodInputs || []
    })
  }, [
    activityLevelValidation,
    activityLevel,
    precursorMaterials,
    preConsumerScrapMaterials,
    emissionReport?.endProductEmittingMethodInputs
  ])

  const precursorAmmountInTonnes = useMemo(() => {
    return precursorMaterials.reduce((acc, curr) => acc + curr.quantityInTonnes, 0) || 0
  }, [precursorMaterials])

  const preConsumerScrapAmmountInTonnes = useMemo(() => {
    return preConsumerScrapMaterials.reduce((acc, curr) => acc + curr.quantityInTonnes, 0) || 0
  }, [preConsumerScrapMaterials])

  const otherMaterialsAmmountInTonnes = useMemo(() => {
    return (
      emissionReport?.endProductEmittingMethodInputs
        ?.filter(input => !input.deletedAt)
        .reduce((acc, curr) => acc + curr.quantityInTonnes, 0) || 0
    )
  }, [emissionReport?.endProductEmittingMethodInputs])

  const isActivityLevelResultValid = useMemo(() => {
    return activityLevelValidationResult.isValid
  }, [activityLevelValidationResult.isValid])

  const hasToBeReEvaluated = useMemo(() => {
    return activityLevelValidation?.hasToBeReEvaluated
  }, [activityLevelValidation?.hasToBeReEvaluated])

  return (
    <>
      <ActivityLevelValidationDialog
        isOpen={open}
        close={closeDialog}
        activityLevel={activityLevel}
        precursorAmmountInTonnes={precursorAmmountInTonnes}
        preConsumerScrapAmmountInTonnes={preConsumerScrapAmmountInTonnes}
        otherMaterialsAmmountInTonnes={otherMaterialsAmmountInTonnes}
        initialValues={activityLevelValidation}
        onSave={onSave}
      />
      <ReportSectionCard
        title={t('report:activityLevelValidation.title')}
        icon={<ActivityLevelValidationIcon className="size-6" />}
        isComplete={isComplete}
        isPending={isPending}
        completionPercentage={isComplete ? 100 : 0}
        isViewMode={isViewMode}
        onEdit={openDialog}
        variant={activityLevelValidation && hasToBeReEvaluated ? 'warning' : 'default'}
        warningText={t('report:activityLevelValidation.warningText')}
        reportButtonText={t('report:activityLevelValidation.validateButtonText')}
        isReportButtonDisabled={!hasAnyPrecursor}
      >
        <div className="space-y-2">
          {!hasAnyPrecursor && (
            <Alert type="warning">{t('report:activityLevelValidation.noPrecursor')}</Alert>
          )}
          {!isActivityLevelResultValid && hasAnyPrecursor && (
            <Alert type="warning">
              {t('report:activityLevelValidation.dialog.validationError', {
                quantity: activityLevelValidationResult.error.quantity,
                conversions: activityLevelValidation?.conversions || 0
              })}
            </Alert>
          )}
          <ActivityLevelValidationReporting
            activityLevel={activityLevel}
            precursorAmmountInTonnes={precursorAmmountInTonnes}
            preConsumerScrapAmmountInTonnes={preConsumerScrapAmmountInTonnes}
            otherMaterialsAmmountInTonnes={otherMaterialsAmmountInTonnes}
            activitiyLevelValidation={activityLevelValidation || null}
          />
        </div>
      </ReportSectionCard>
    </>
  )
}
